import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchChildEmails, actChildEmail, submitFeedback } from "@/lib/api";
import { DETECTED } from "@/constants/testIds";
import { formatDate, classFor, categoryIcon } from "@/lib/format";
import * as LucideIcons from "lucide-react";
import { ThumbsUp, ThumbsDown, ShieldCheck, Ban, Filter, Sparkles } from "lucide-react";
import { toast } from "sonner";
import FeedbackDialog from "@/components/FeedbackDialog";

const CATS = ["All", "School", "Sports", "Camp", "Activity", "Appointment", "Payment", "Form", "Transportation", "Event"];

export default function DetectedEmails() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(params.get("category") || "All");
  const [feedbackTarget, setFeedbackTarget] = useState(null);

  const load = async () => {
    const data = await fetchChildEmails(filter === "All" ? {} : { category: filter });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const summary = useMemo(() => ({
    family_items: items.filter(i => i.status !== "rejected").length,
    needs_review: items.filter(i => i.status === "needs_review").length,
    events: items.filter(i => i.event_date).length,
  }), [items]);

  const act = async (id, action) => {
    await actChildEmail(id, action);
    toast.success({
      keep: "Kept. We'll keep showing this.",
      reject: "Rejected. Removed from your list.",
      always_include: "Sender added to trusted list.",
      never_include: "Sender added to blocked list.",
    }[action]);
    load();
  };

  const sendCorrect = async (id) => {
    await submitFeedback({ email_id: id, verdict: "correct" });
    toast.success("Thanks — you just trained KidHQ.");
    load();
  };

  return (
    <div data-testid={DETECTED.page} className="space-y-10">
      <header className="fcc-card overflow-hidden grid lg:grid-cols-12">
        <div className="lg:col-span-8 p-8 sm:p-10">
          <div className="flex flex-wrap gap-3 items-center">
            <span className="fcc-pill fcc-chip-emma">Family inbox</span>
            <span className="fcc-pill" style={{ background: "var(--fcc-peach)", color: "var(--fcc-ink)" }}>Emma + Noah</span>
          </div>
          <h1 className="font-display text-[40px] sm:text-[52px] leading-[1.05] text-[var(--fcc-ink)] mt-5">Detected Child-Related Emails</h1>
          <p className="text-[var(--fcc-ink-soft)] mt-4 max-w-2xl leading-relaxed">A calmer pass over the inbox: soccer changes, camp forms, school reminders, appointments, payments, and the occasional false alarm.</p>
          <div className="mt-7 grid grid-cols-3 gap-4 max-w-xl">
            <Stat label="Family items" value={summary.family_items} Icon={LucideIcons.CheckCircle2} />
            <Stat label="Needs review" value={summary.needs_review} Icon={LucideIcons.MailQuestion} />
            <Stat label="Events found" value={summary.events} Icon={LucideIcons.CalendarRange} />
          </div>
        </div>
        <aside className="lg:col-span-4 p-7 sm:p-8 bg-[var(--fcc-cream-2)] border-l border-[var(--fcc-border)] space-y-3">
          <p className="label-overline">Your crew</p>
          <div className="bg-white border border-[var(--fcc-border)] rounded-2xl p-3 flex gap-3 items-center">
            <span className="w-9 h-9 rounded-xl bg-[var(--fcc-emma)] flex items-center justify-center"><LucideIcons.User className="w-4 h-4 text-[var(--fcc-green)]" /></span>
            <div><p className="font-semibold text-[var(--fcc-ink)]">Emma</p><p className="text-xs text-[var(--fcc-muted)]">3rd · Oak Ridge Elementary</p></div>
          </div>
          <div className="bg-white border border-[var(--fcc-border)] rounded-2xl p-3 flex gap-3 items-center">
            <span className="w-9 h-9 rounded-xl bg-[var(--fcc-noah)] flex items-center justify-center"><LucideIcons.User className="w-4 h-4 text-[var(--fcc-green)]" /></span>
            <div><p className="font-semibold text-[var(--fcc-ink)]">Noah</p><p className="text-xs text-[var(--fcc-muted)]">6th · Oak Ridge Elementary</p></div>
          </div>
          <div className="bg-[var(--fcc-ink)] text-white rounded-2xl p-4">
            <p className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Sender learning</p>
            <p className="text-xs text-white/80 mt-2 leading-relaxed">Trusted coaches, camps, schools, studios — and blocked senders — shape the next import.</p>
          </div>
        </aside>
      </header>

      <div className="flex items-center gap-2 overflow-x-auto tab-scroll">
        <Filter className="w-4 h-4 text-[var(--fcc-muted)] shrink-0" />
        {CATS.map(c => (
          <button key={c} onClick={() => { setFilter(c); setParams(c === "All" ? {} : { category: c }); }}
            className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap ${filter === c ? "bg-[var(--fcc-ink)] text-white" : "bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink-soft)]"}`}>
            {c}
          </button>
        ))}
      </div>

      <ul className="space-y-4">
        {items.map(ce => {
          const Icon = LucideIcons[categoryIcon(ce.category)] || LucideIcons.Sparkles;
          return (
            <li key={ce.id} data-testid={DETECTED.emailCard} className="fcc-card p-6">
              <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-9">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-xl bg-[var(--fcc-cream-2)] flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[var(--fcc-green)]" /></span>
                    <div className="flex-1">
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="fcc-pill" style={{ background: "var(--fcc-emma)", color: "var(--fcc-green)" }}>{ce.category}</span>
                        <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]">{Math.round(ce.confidence * 100)}% match</span>
                        <span className={`fcc-pill ${classFor(ce.priority)}`}>{ce.priority}</span>
                        <span className="fcc-pill bg-[var(--fcc-green-soft)] text-[var(--fcc-green)]">
                          {ce.status === "rejected" ? "Rejected" : ce.status === "needs_review" ? "Needs review" : "Kept"}
                        </span>
                      </div>
                      <h3 className="font-display text-xl text-[var(--fcc-ink)] mt-3 leading-snug">{ce.subject}</h3>
                      <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 leading-relaxed">{ce.summary}</p>
                      <div className="flex flex-wrap gap-2 mt-3 text-xs text-[var(--fcc-muted)]">
                        <span className="inline-flex items-center gap-1"><LucideIcons.Building2 className="w-3 h-3" />{ce.organization}</span>
                        {ce.due_date && <span className="inline-flex items-center gap-1"><LucideIcons.Clock className="w-3 h-3" />Due {formatDate(ce.due_date)}</span>}
                        {ce.event_date && <span className="inline-flex items-center gap-1"><LucideIcons.Calendar className="w-3 h-3" />Event {formatDate(ce.event_date)}</span>}
                        {ce.location && <span className="inline-flex items-center gap-1"><LucideIcons.MapPin className="w-3 h-3" />{ce.location}</span>}
                        {ce.payment_amount && <span className="inline-flex items-center gap-1"><LucideIcons.DollarSign className="w-3 h-3" />${ce.payment_amount.toFixed(2)}</span>}
                      </div>
                      <div className="rounded-2xl bg-[var(--fcc-cream-2)] p-4 mt-4">
                        <p className="label-overline">Why it matters</p>
                        <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">recognized organization: {ce.organization} · mentions {ce.child_name} · classified as {ce.category}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-3 flex flex-col gap-2">
                  <button data-testid={DETECTED.keep} onClick={() => act(ce.id, "keep")} className="fcc-btn-primary justify-center text-sm py-2.5"><ThumbsUp className="w-4 h-4" /> Keep</button>
                  <button data-testid={DETECTED.reject} onClick={() => act(ce.id, "reject")} className="fcc-btn-outline justify-center text-sm py-2.5"><ThumbsDown className="w-4 h-4" /> Reject</button>
                  <button data-testid={DETECTED.alwaysInclude} onClick={() => act(ce.id, "always_include")} className="rounded-xl bg-[var(--fcc-green-soft)] text-[var(--fcc-green)] text-sm py-2.5 font-semibold inline-flex items-center gap-2 justify-center"><ShieldCheck className="w-4 h-4" /> Always include</button>
                  <button data-testid={DETECTED.neverInclude} onClick={() => act(ce.id, "never_include")} className="rounded-xl bg-[var(--fcc-red-soft)] text-[#8B241D] text-sm py-2.5 font-semibold inline-flex items-center gap-2 justify-center"><Ban className="w-4 h-4" /> Never include</button>
                  <div className="mt-3 border-t border-[var(--fcc-border)] pt-3">
                    <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Train KidHQ</p>
                    {ce.feedback_verdict ? (
                      <span className="fcc-pill bg-[var(--fcc-green-soft)] text-[var(--fcc-green)] w-full justify-center">You trained on this · {ce.feedback_verdict}</span>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <button data-testid={DETECTED.feedbackCorrect} onClick={() => sendCorrect(ce.id)} className="rounded-xl bg-white border border-[var(--fcc-border)] text-sm py-2 font-semibold inline-flex items-center gap-1 justify-center hover:bg-[var(--fcc-green-soft)] hover:border-[var(--fcc-green)] hover:text-[var(--fcc-green)] transition">👍 Correct</button>
                        <button data-testid={DETECTED.feedbackIncorrect} onClick={() => setFeedbackTarget(ce)} className="rounded-xl bg-white border border-[var(--fcc-border)] text-sm py-2 font-semibold inline-flex items-center gap-1 justify-center hover:bg-[var(--fcc-red-soft)] hover:border-[var(--fcc-red)] hover:text-[#8B241D] transition">👎 Incorrect</button>
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--fcc-muted)] mt-1 text-center break-all">{ce.sender_email}</div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {feedbackTarget && (
        <FeedbackDialog
          email={feedbackTarget}
          onClose={() => setFeedbackTarget(null)}
          onSubmitted={() => { setFeedbackTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, Icon }) {
  return (
    <div className="border border-[var(--fcc-border)] rounded-2xl bg-white p-4">
      <span className="w-9 h-9 rounded-xl bg-[var(--fcc-cream-2)] flex items-center justify-center"><Icon className="w-4 h-4 text-[var(--fcc-green)]" /></span>
      <div className="font-display text-3xl text-[var(--fcc-ink)] mt-3">{value}</div>
      <div className="text-xs text-[var(--fcc-muted)] mt-1">{label}</div>
    </div>
  );
}
